<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { AlertCircle, CheckCircle, Info, X } from 'lucide-vue-next'
  import { Checkbox } from '@/components/ui/checkbox'
  import { Button } from '@/components/ui/button'
  import InformationalBanner from '@/components/ui/informational-banner/InformationalBanner.vue'
  import { useToast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables'
  import { useAiAssistantBeta, type FeatureId } from './useAiAssistantBeta'

  const emit = defineEmits<{
    close: []
  }>()

  // Defense-in-depth: even if a future caller mounts this view without checking the
  // feature flag at the parent, the component renders nothing when the flag is off.
  const {
    activate,
    setActivatedFeature,
    activatedFeatures,
    isActivated,
    isAdmin,
    isFeatureEnabled,
    isLoading
  } = useAiAssistantBeta()
  const { toast } = useToast()

  const { t } = useTranslation({
    en: {
      title: 'Meet the new JUPUS AI Assistant',
      subtitle: 'Now in beta — free for a limited time.',
      body: 'Let JUPUS handle the work for you — autonomously. The assistant beta can execute a limited number of tasks on its own or draft your first email autonomously. All suggestions are generated based on your past interactions on our platform.',
      skillsHeader: 'Please select the skills you want to activate',
      skillAssignLawFields: 'Assign law fields',
      skillAssignClerks: 'Assign clerks',
      skillSuggestEmail: 'Suggest the first client email',
      disclaimerLine1: 'This is a beta release so AI can make mistakes.',
      disclaimerLine2: 'You can change selected features or opt out anytime.',
      activate: 'Activate AI assistant',
      activated: 'AI assistant activated',
      adminOnly: 'Only admin users can change AI assistant settings',
      close: 'Close',
      errorTitle: "Couldn't activate the AI assistant. Please try again.",
      errorSetSkill: "Couldn't update the skill. Please try again.",
      toastEnabled: 'AI assistant will automatically {action} for future inquiries',
      toastDisabled: 'AI assistant will not {action} for future inquiries',
      actionAssignLawFields: 'assign law fields',
      actionAssignClerks: 'assign clerks',
      actionSuggestEmail: 'suggest the first client emails'
    },
    de: {
      title: 'Der neue JUPUS AI Assistant',
      subtitle: 'Jetzt in der Beta — für begrenzte Zeit kostenlos.',
      body: 'Lass JUPUS die Arbeit für dich erledigen — autonom. Die Assistant-Beta kann eine begrenzte Anzahl von Aufgaben eigenständig ausführen oder deine erste E-Mail autonom entwerfen. Alle Vorschläge basieren auf deinen bisherigen Interaktionen auf unserer Plattform.',
      skillsHeader: 'Bitte wähle die Funktionen aus, die du aktivieren möchtest',
      skillAssignLawFields: 'Rechtsgebiete zuweisen',
      skillAssignClerks: 'Sachbearbeiter zuweisen',
      skillSuggestEmail: 'Erste Mandanten-E-Mail vorschlagen',
      disclaimerLine1: 'Dies ist eine Beta-Version, daher kann die KI Fehler machen.',
      disclaimerLine2: 'Du kannst die ausgewählten Funktionen jederzeit ändern oder dich abmelden.',
      activate: 'AI Assistant aktivieren',
      activated: 'AI Assistant aktiviert',
      adminOnly: 'Nur Administratoren können die AI-Assistant-Einstellungen ändern',
      close: 'Schließen',
      errorTitle: 'AI Assistant konnte nicht aktiviert werden. Bitte versuche es erneut.',
      errorSetSkill: 'Funktion konnte nicht aktualisiert werden. Bitte versuche es erneut.',
      toastEnabled: 'AI Assistant wird automatisch {action} für zukünftige Anfragen',
      toastDisabled: 'AI Assistant wird nicht {action} für zukünftige Anfragen',
      actionAssignLawFields: 'Rechtsgebiete zuweisen',
      actionAssignClerks: 'Sachbearbeiter zuweisen',
      actionSuggestEmail: 'erste Mandanten-E-Mails vorschlagen'
    }
  })

  const skills: ReadonlyArray<{
    id: FeatureId
    labelKey: 'skillAssignLawFields' | 'skillAssignClerks' | 'skillSuggestEmail'
    actionKey: 'actionAssignLawFields' | 'actionAssignClerks' | 'actionSuggestEmail'
  }> = [
    {
      id: 'assign_law_fields',
      labelKey: 'skillAssignLawFields',
      actionKey: 'actionAssignLawFields'
    },
    { id: 'assign_clerks', labelKey: 'skillAssignClerks', actionKey: 'actionAssignClerks' },
    {
      id: 'suggest_first_client_email',
      labelKey: 'skillSuggestEmail',
      actionKey: 'actionSuggestEmail'
    }
  ]

  // Pre-activation working set — handed to activate() on click. Once activated, the source
  // of truth flips to `activatedFeatures` from the composable so this surface reflects what
  // the org actually opted into (a subset, after admin chose on the settings page or modal).
  const localSelected = ref<FeatureId[]>(skills.map((s) => s.id))

  watch(isActivated, (active) => {
    if (!active) localSelected.value = skills.map((s) => s.id)
  })

  const selectedFeatures = computed<readonly FeatureId[]>(() =>
    isActivated.value ? activatedFeatures.value : localSelected.value
  )

  const canActivate = computed(
    () => localSelected.value.length > 0 && isAdmin.value && !isActivated.value && !isLoading.value
  )
  const onlyOneSelected = computed(() => selectedFeatures.value.length === 1)

  function isSelected(id: FeatureId) {
    return selectedFeatures.value.includes(id)
  }

  function isCheckboxDisabled(id: FeatureId) {
    if (!isAdmin.value) return true
    if (isLoading.value) return true
    // Last-locked: keep at least one feature selected (matches the settings page rule).
    // Full opt-out happens via the deactivate flow on the settings page, not by toggling
    // the last skill off here.
    return onlyOneSelected.value && isSelected(id)
  }

  async function toggle(id: FeatureId, checked: boolean) {
    if (isActivated.value) {
      // Post-activation: same quick-edit behaviour as the settings page — call the backend
      // and adopt the response state. Success toast fires only after the backend confirms,
      // so an error path doesn't leave a misleading "skill enabled" toast on screen.
      try {
        await setActivatedFeature(id, checked)
      } catch {
        toast({
          variant: 'destructive',
          icon: AlertCircle,
          title: t('errorSetSkill')
        })
        return
      }
      const skill = skills.find((s) => s.id === id)
      if (skill) {
        toast({
          variant: 'neutralSuccess',
          icon: CheckCircle,
          iconClass: 'text-[#059669]',
          title: t(checked ? 'toastEnabled' : 'toastDisabled', {
            action: t(skill.actionKey)
          })
        })
      }
      return
    }
    // Pre-activation: local working set, handed to activate() on click.
    if (checked) {
      if (!localSelected.value.includes(id)) {
        localSelected.value = [...localSelected.value, id]
      }
    } else {
      localSelected.value = localSelected.value.filter((f) => f !== id)
    }
  }

  async function handleActivate() {
    if (!canActivate.value) return
    try {
      await activate([...localSelected.value])
    } catch {
      toast({
        variant: 'destructive',
        icon: AlertCircle,
        title: t('errorTitle')
      })
    }
  }
</script>

<template>
  <div
    v-if="isFeatureEnabled"
    class="flex flex-1 flex-col self-stretch overflow-hidden"
  >
    <div class="flex h-14 items-center justify-end gap-6 px-4">
      <button
        type="button"
        :aria-label="t('close')"
        class="flex h-6 w-6 items-center justify-center text-[#1F2937] hover:opacity-80"
        @click="emit('close')"
      >
        <X
          class="h-6 w-6"
          :stroke-width="1.25"
        />
      </button>
    </div>

    <div class="flex flex-1 flex-col items-start gap-4 overflow-y-auto px-4 pb-10 pt-4">
      <div class="flex flex-1 flex-col items-start gap-6 self-stretch">
        <div class="flex flex-col self-stretch">
          <h2 class="text-xl font-semibold leading-8 text-[#1F2937]">{{ t('title') }}</h2>
          <p class="text-sm font-normal leading-5 text-[#6B7280]">{{ t('subtitle') }}</p>
        </div>

        <p class="self-stretch text-sm font-normal leading-5 text-[#1F2937]">
          {{ t('body') }}
        </p>

        <div class="flex flex-col items-start gap-2 self-stretch">
          <p class="self-stretch text-sm font-semibold leading-5 text-[#1F2937]">
            {{ t('skillsHeader') }}
          </p>
          <div class="flex flex-col items-start gap-1 self-stretch">
            <label
              v-for="skill in skills"
              :key="skill.id"
              class="inline-flex items-center gap-2"
              :class="isCheckboxDisabled(skill.id) ? 'cursor-not-allowed' : 'cursor-pointer'"
            >
              <Checkbox
                :checked="isSelected(skill.id)"
                :disabled="isCheckboxDisabled(skill.id)"
                @update:checked="(v: boolean) => toggle(skill.id, v)"
              />
              <span class="text-sm font-normal leading-5 text-[#1F2937]">
                {{ t(skill.labelKey) }}
              </span>
            </label>
          </div>
        </div>

        <p class="text-xs font-normal leading-4 text-[#6B7280]">
          {{ t('disclaimerLine1') }}
          <br />
          {{ t('disclaimerLine2') }}
        </p>

        <div
          v-if="!isActivated"
          class="mt-auto flex flex-col items-start gap-4 self-stretch"
        >
          <InformationalBanner
            v-if="!isAdmin"
            :icon="Info"
          >
            {{ t('adminOnly') }}
          </InformationalBanner>
          <Button
            :disabled="!canActivate"
            class="h-10 w-full rounded-lg px-4 text-base font-medium leading-6
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
            @click="handleActivate"
          >
            {{ t('activate') }}
          </Button>
        </div>
        <p
          v-else
          class="mt-auto self-stretch text-center text-sm font-medium leading-5 text-[#9333EA]"
        >
          {{ t('activated') }}
        </p>
      </div>
    </div>
  </div>
</template>
